import { useState } from "react";
import { jobs, isJobGroup, Job, JobGroup, Role } from "../data/portfolio";
import "../styles/Portfolio.css";

// Parse "Mon YYYY" format to Date
const parseDate = (dateStr: string): Date => {
  if (dateStr === "Present") return new Date();
  const [month, year] = dateStr.split(" ");
  const monthIndex = new Date(`${month} 1, 2000`).getMonth();
  return new Date(parseInt(year), monthIndex);
};

// Calculate duration between two dates
const calculateDuration = (startStr: string, endStr: string): string => {
  const start = parseDate(startStr);
  const end = parseDate(endStr);

  let months = (end.getFullYear() - start.getFullYear()) * 12;
  months += end.getMonth() - start.getMonth();
  months = Math.max(1, months); // At least 1 month

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years === 0) {
    return `${remainingMonths} mo${remainingMonths !== 1 ? "s" : ""}`;
  } else if (remainingMonths === 0) {
    return `${years} yr${years !== 1 ? "s" : ""}`;
  } else {
    return `${years} yr${years !== 1 ? "s" : ""} ${remainingMonths} mo${remainingMonths !== 1 ? "s" : ""}`;
  }
};

// Get total duration for a job group
const getGroupDuration = (roles: Role[]): string => {
  const allDates = roles.flatMap((r) => [r.startDate, r.endDate]);
  const parsedDates = allDates.map((d) => parseDate(d));
  const earliest = new Date(Math.min(...parsedDates.map((d) => d.getTime())));
  const latest = new Date(Math.max(...parsedDates.map((d) => d.getTime())));

  const earliestStr = `${earliest.toLocaleString("en-US", { month: "short" })} ${earliest.getFullYear()}`;
  const latestStr =
    latest.getTime() === parseDate("Present").getTime()
      ? "Present"
      : `${latest.toLocaleString("en-US", { month: "short" })} ${latest.getFullYear()}`;

  return calculateDuration(earliestStr, latestStr);
};

const ExperienceSection = () => {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const toggleExpand = (key: string, event: React.MouseEvent) => {
    if ((event.target as HTMLElement).tagName === "A") return;
    setExpandedKey(expandedKey === key ? null : key);
  };

  const renderSingleJob = (job: Job, index: number) => {
    const key = `job-${index}`;
    const isExpanded = expandedKey === key;
    const duration = calculateDuration(job.startDate, job.endDate);

    return (
      <div
        className={`card experience-card ${isExpanded ? "expanded" : ""} ${job.highlights ? "clickable" : ""}`}
        key={key}
        onClick={(e) => job.highlights && toggleExpand(key, e)}
      >
        <div className="experience-card__header">
          <div className="experience-card__content">
            <h3 className="card-title">{job.title}</h3>
            {job.companyUrl ? (
              <a
                href={job.companyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="card-subtitle card-link"
              >
                {job.company}
              </a>
            ) : (
              <p className="card-subtitle">{job.company}</p>
            )}
          </div>
          <div className="experience-card__dates">
            <span className="card-meta">
              {job.startDate} - {job.endDate}
            </span>
            <span className="card-duration">{duration}</span>
          </div>
        </div>
        {job.highlights && (
          <div className={`experience-highlights-wrapper ${isExpanded ? "open" : ""}`}>
            <ul className="experience-highlights">
              {job.highlights.map((highlight, hIndex) => (
                <li key={hIndex}>{highlight}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderJobGroup = (group: JobGroup, index: number) => {
    const key = `group-${index}`;
    const isExpanded = expandedKey === key;
    const totalDuration = getGroupDuration(group.roles);
    const hasHighlights = group.roles.some((r) => r.highlights);

    return (
      <div
        className={`card experience-card experience-card--group ${isExpanded ? "expanded" : ""} ${hasHighlights ? "clickable" : ""}`}
        key={key}
        onClick={(e) => hasHighlights && toggleExpand(key, e)}
      >
        <div className="experience-group__header">
          <div className="experience-group__info">
            {group.companyUrl ? (
              <a
                href={group.companyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="experience-group__company card-link"
              >
                {group.company}
              </a>
            ) : (
              <span className="experience-group__company">{group.company}</span>
            )}
            <span className="experience-group__duration">{totalDuration}</span>
          </div>
        </div>
        <div className="experience-timeline">
          {group.roles.map((role, roleIndex) => {
            const isLast = roleIndex === group.roles.length - 1;
            const duration = calculateDuration(role.startDate, role.endDate);

            return (
              <div
                className={`experience-timeline__item ${isLast ? "last" : ""}`}
                key={roleIndex}
              >
                <div className="experience-timeline__dot" />
                <div className="experience-timeline__content">
                  <div className="experience-card__header">
                    <h3 className="card-title">{role.title}</h3>
                    <div className="experience-card__dates">
                      <span className="card-meta">
                        {role.startDate} - {role.endDate}
                      </span>
                      <span className="card-duration">{duration}</span>
                    </div>
                  </div>
                  {role.highlights && (
                    <div className={`experience-highlights-wrapper ${isExpanded ? "open" : ""}`}>
                      <ul className="experience-highlights">
                        {role.highlights.map((highlight, hIndex) => (
                          <li key={hIndex}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <section id="experience" className="portfolio-section">
      <div className="portfolio-container">
        <h2 className="section-label">Experience</h2>
        <div className="card-list">
          {jobs.map((entry, index) =>
            isJobGroup(entry)
              ? renderJobGroup(entry, index)
              : renderSingleJob(entry, index)
          )}
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
